import { breakpoint } from "@porsche-design-system/components-js/styles";
import { Environment } from "../entities/environment";
import { navStateStore } from "../state/nav-state-store";
import configJSON from "../config.json";
import { Config } from "../entities/config";
import {
  LOADING_STATUS,
  LOGGED_IN_USER_NO_NAME_STATUS,
  LOGGED_OUT_USER_STATUS,
  NavigationMode,
  NavigationModeType,
  NavigationTheme,
  NavigationThemeType,
} from "./constants";
import { Application } from "./constants/app";
import { Screensize } from "./constants/screensize";

let rootElement: HTMLElement | null = null;

export function getRootElement(): HTMLElement {
  if (rootElement === null) {
    throw new Error("No root element present");
  }
  return rootElement;
}

export function loadRootElement(element: HTMLElement): void {
  rootElement = element;
}

export function getHTMLElementFromRoot(selector: string): HTMLElement | null {
  return getRootElement().querySelector(selector);
}

export function isPromise<T>(value: unknown): value is Promise<T> {
  return (
    Boolean(value) &&
    typeof (value as Promise<T>).then === "function" &&
    (value as Promise<T>)[Symbol.toStringTag] === "Promise"
  );
}

export function isString(test: unknown): test is string {
  return typeof test === "string" && test.length > 0;
}

/* istanbul ignore next */
export function getHTMLElement(el: unknown): HTMLElement | null {
  return el instanceof HTMLElement ? el : null;
}

export function hasKey<O>(obj: O, key: PropertyKey): key is keyof O {
  return obj && typeof obj === "object" && key in obj;
}

export function getString(test: unknown): string {
  return typeof test === "string" ? test : "";
}

export function camelToKebapCase(camelString: string): string {
  return camelString.replace(/([A-Z])/g, (g) => `-${g[0].toLowerCase()}`);
}

// eslint-disable-next-line
export function shallowCopySpecifiedAttributesWithDefault<
  S extends { [s: string]: unknown } | ArrayLike<unknown>,
  C extends { [s: string]: unknown } | ArrayLike<unknown>,
>(
  sourceObject: S,
  copyObject: C,
  accessor: (object: S, key: keyof C) => unknown = (object, key) =>
    (object as Record<string | number | symbol, unknown>)[key],
) {
  return Object.entries(copyObject).reduce((values, [key, value]) => {
    const valueFromSource = accessor(sourceObject, key as keyof C);
    if (typeof valueFromSource === "string") {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      values[key as keyof C] = valueFromSource as any;
    } else {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      values[key as keyof C] = value as any;
    }
    return values;
  }, {} as C);
}

// Persist the config object to avoid re-reading an outdated config.json file
const configs = {} as Record<Environment, Config>;
export function getConfig(env: Environment): Config {
  const { locale } = navStateStore.state;
  let localConfig = configJSON[env] as Config;
  if (env in configs) {
    localConfig = configs[env];
  } else {
    configs[env] = localConfig;
  }

  if (locale === "en-CN" || locale === "zh-CN") {
    return {
      ...localConfig,
      CONTENT_URL: localConfig.CONTENT_CN_URL,
      ASSETS_URL: localConfig.ASSETS_CN_URL,
      PHN_DRAWER_CDN_URL: localConfig.PHN_DRAWER_CN_CDN_URL,
      FOOTER_ASSETS_URL: localConfig.FOOTER_ASSETS_CN_URL,
    };
  }
  return localConfig;
}

export function IsLoading(loggedInState: string): boolean {
  return loggedInState === LOADING_STATUS;
}

export function IsLoggedOut(loggedInState: string): boolean {
  return loggedInState === LOGGED_OUT_USER_STATUS;
}

export function IsLoggedIn(loggedInState: string): boolean {
  return !IsLoading(loggedInState) && !IsLoggedOut(loggedInState);
}

export function getUsername(
  loggedInState: string,
  loggedInText: string,
): string {
  return loggedInState === LOGGED_IN_USER_NO_NAME_STATUS
    ? loggedInText
    : loggedInState;
}

export function constructCacheKey(...args: string[]): string {
  return args.join("---");
}

/* istanbul ignore next */
export function getHTMLInputElement(el: unknown): HTMLInputElement | null {
  return el instanceof HTMLInputElement ? el : null;
}

/* istanbul ignore next */
export function focusOnElement(el: unknown): void {
  getHTMLElement(el)?.focus();
}

export function isDesktop(currentBreakpoint: number): boolean {
  return currentBreakpoint >= breakpoint.s;
}

export function isMobile(currentBreakpoint: number): boolean {
  return currentBreakpoint <= breakpoint.xs;
}

export function addQueryParamsToUrl(
  url: string,
  params: Record<string, string>,
): string {
  return `${url}${url.includes("?") ? "&" : "?"}${new URLSearchParams(params).toString()}`;
}

export function isModelFuelTypeGasoline(model: string): boolean {
  const lowerCase = model.toLowerCase();
  return ["718", "911", "macan"].includes(lowerCase);
}

export function getImageSize(currentBreakpoint: number): number {
  switch (currentBreakpoint) {
    case breakpoint.xxl:
    case breakpoint.xl:
      return Screensize.XXL;
    case breakpoint.l:
      return Screensize.L;
    case breakpoint.m:
      return Screensize.M;
    case breakpoint.s:
      return Screensize.S;
    case breakpoint.xs:
      return Screensize.XS;
    default:
      return Screensize.base;
  }
}

export const getRightTheme = (
  theme: NavigationThemeType,
  mode: NavigationModeType,
): NavigationThemeType => {
  if (mode === NavigationMode.transparent || mode === NavigationMode.hero) {
    return theme;
  }
  return NavigationTheme.light;
};

export function isModelName(text: string): boolean {
  return ["718", "911", "Taycan", "Macan", "Panamera", "Cayenne"].includes(
    text,
  );
}

export function IsKeyboardClick(e: MouseEvent): boolean {
  return e.x === 0 && e.y === 0;
}

export function getIconsStrategyApp(
  iconsStrategies: Record<string, any>,
  app: string,
): string {
  if (app in iconsStrategies) {
    return app;
  }
  return Application.default;
}
